// Appearance (light/dark) theme. Dark is the default; light is a print-friendly,
// low-ink scheme. The choice is a single attribute on <html> — every component
// re-themes for free because all colors are CSS custom properties (see
// :root / :root[data-theme="light"] in styles.css). Persisted to localStorage
// (already used by MSAL, so this stays offline-safe).

export type Theme = 'dark' | 'light';

const KEY = 'bt-theme';

/** Read the saved theme; anything but an explicit 'light' is dark (the default). */
export function getTheme(): Theme {
  try {
    return localStorage.getItem(KEY) === 'light' ? 'light' : 'dark';
  } catch {
    return 'dark'; // storage blocked (private mode) → default
  }
}

/** Reflect a theme onto <html> so the CSS token overrides take effect. */
export function applyTheme(theme: Theme): void {
  document.documentElement.dataset.theme = theme;
}

/** Persist + apply. Swallows storage failures (private mode) — apply still runs. */
export function setTheme(theme: Theme): void {
  try {
    localStorage.setItem(KEY, theme);
  } catch {
    /* storage blocked — session-only theme is fine */
  }
  applyTheme(theme);
}

/** Apply the saved theme. Call BEFORE first paint to avoid a flash of dark. */
export function initTheme(): void {
  applyTheme(getTheme());
}
